import type {
  CancelMatchRequest,
  CompetitionPhase,
  CompetitionSeasonView,
  CreateMatchRequest,
  MatchCandidateEventView,
  MatchHomeAway,
  MatchStatus,
  MatchView,
  SupportedLocale,
  UpdateMatchRequest,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';
import { formatCalendarDate } from './athletes';

export const matchStatusKeys: Readonly<Record<MatchStatus, TranslationKey>> = {
  scheduled: 'match.status.scheduled',
  completed: 'match.status.completed',
  cancelled: 'match.status.cancelled',
};

export const matchHomeAwayKeys: Readonly<Record<MatchHomeAway, TranslationKey>> = {
  home: 'match.homeAway.home',
  away: 'match.homeAway.away',
};

export const matchPhaseKeys: Readonly<Record<CompetitionPhase, TranslationKey>> = {
  regular: 'competitions.phase.regular',
  playoff: 'competitions.phase.playoff',
  tournament: 'competitions.phase.tournament',
};

/** The longest opponent label the server and the database accept. */
export const maximumOpponentLabelLength = 100;

export interface MatchForm {
  eventId: string;
  teamId: string;
  phase: CompetitionPhase | '';
  opponentLabel: string;
  homeAway: MatchHomeAway;
}

export const emptyMatchForm: MatchForm = {
  eventId: '',
  teamId: '',
  phase: '',
  opponentLabel: '',
  homeAway: 'home',
};

/** A competition with one phase needs no choice: the form starts with that phase. */
export function initialMatchForm(competition: Pick<CompetitionSeasonView, 'phases'>): MatchForm {
  const [onlyPhase, ...others] = competition.phases;
  return {
    ...emptyMatchForm,
    phase: onlyPhase !== undefined && others.length === 0 ? onlyPhase : '',
  };
}

function trimmedLabel(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

/** The create request, or null while the form is incomplete or the label is too long. */
export function matchRequest(
  form: MatchForm,
  competition: Pick<CompetitionSeasonView, 'id' | 'phases'>,
): CreateMatchRequest | null {
  const opponentLabel = trimmedLabel(form.opponentLabel);
  if (
    form.eventId === '' ||
    form.teamId === '' ||
    form.phase === '' ||
    !competition.phases.includes(form.phase) ||
    opponentLabel === '' ||
    opponentLabel.length > maximumOpponentLabelLength
  ) {
    return null;
  }
  return {
    competitionSeasonId: competition.id,
    eventId: form.eventId,
    teamId: form.teamId,
    phase: form.phase,
    opponentLabel,
    homeAway: form.homeAway,
  };
}

export interface MatchCorrectionForm {
  phase: CompetitionPhase;
  opponentLabel: string;
  homeAway: MatchHomeAway;
}

export function matchCorrectionFormOf(match: MatchView): MatchCorrectionForm {
  return { phase: match.phase, opponentLabel: match.opponentLabel, homeAway: match.homeAway };
}

/**
 * Only the fields that differ from the record are sent. Null when nothing changed or the label is
 * empty or too long, so the save button stays off.
 */
export function matchCorrectionRequest(
  form: MatchCorrectionForm,
  match: MatchView,
): UpdateMatchRequest | null {
  const opponentLabel = trimmedLabel(form.opponentLabel);
  if (opponentLabel === '' || opponentLabel.length > maximumOpponentLabelLength) return null;
  const request: UpdateMatchRequest = {};
  if (form.phase !== match.phase) request.phase = form.phase;
  if (opponentLabel !== match.opponentLabel) request.opponentLabel = opponentLabel;
  if (form.homeAway !== match.homeAway) request.homeAway = form.homeAway;
  return Object.keys(request).length === 0 ? null : request;
}

/** The cancellation request, or null while no reason is written. */
export function cancelMatchRequest(reason: string): CancelMatchRequest | null {
  const trimmed = reason.trim();
  return trimmed === '' ? null : { reason: trimmed };
}

/** The label of an event in the picker: day, local start time and title. */
export function candidateEventLabel(
  event: MatchCandidateEventView,
  locale: SupportedLocale,
): string {
  return `${formatCalendarDate(event.date, locale)} ${event.startTime} · ${event.title}`;
}

const statusOrder: Readonly<Record<MatchStatus, number>> = {
  scheduled: 0,
  completed: 1,
  cancelled: 2,
};

/** Scheduled matches first, then completed, then cancelled; the server's order within each group. */
export function scheduledFirst(matches: readonly MatchView[]): MatchView[] {
  return matches
    .map((match, position) => ({ match, position }))
    .sort(
      (left, right) =>
        statusOrder[left.match.status] - statusOrder[right.match.status] ||
        left.position - right.position,
    )
    .map(({ match }) => match);
}

export function matchPath(base: string, matchId?: string, action?: 'complete' | 'cancel'): string {
  const root = `${base}/matches`;
  if (matchId === undefined) return root;
  const one = `${root}/${encodeURIComponent(matchId)}`;
  return action === undefined ? one : `${one}/${action}`;
}
